import { useState, useMemo } from "react";
import {
  Loader2,
  AlertTriangle,
  Leaf,
  Layers,
  Info as InfoIcon,
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { supabase } from '../lib/supabaseClient.ts';
import { useLanguage } from '../contexts/LanguageContext.tsx';
import { useAsyncData } from '../hooks/useAsyncData';

const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

type Farm = { id: string; name: string };
type SoilReading = {
  farmId: string;
  nitrogen: number;
  phosphorus: number;
  potassium: number;
  timestamp: string;
};

const getStatusColors = (isLow: boolean) => {
  return isLow ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800";
};

const getNutrientBarColor = (nutrient: "N" | "P" | "K") => {
  switch (nutrient) {
    case "N": return "bg-green-500";
    case "P": return "bg-yellow-500";
    case "K": return "bg-blue-500";
    default: return "bg-gray-400";
  }
};

export default function SoilManagementPanel({
  user,
  onSignIn,
}: {
  user: any;
  onSignIn?: () => void;
}) {
  const { t } = useLanguage();
  const [readings, setReadings] = useState<Record<string, SoilReading>>({});

  // Fetch farms data
  const { data: farms, loading, error } = useAsyncData(
    async () => {
      if (!user?.id) return [];
      
      const { data, error: fetchError } = await supabase
        .from("farms")
        .select("id, name")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw new Error(fetchError.message);
      
      const farmsList = (data || []).map(farm => ({
        id: farm.id as string,
        name: farm.name as string
      }));

      // Fetch soil readings for farms
      const readingsMap: Record<string, SoilReading> = {};
      
      // Generate fallback data for demo
      farmsList.forEach(farm => {
        readingsMap[farm.id] = {
          farmId: farm.id,
          nitrogen: Math.round(15 + Math.random() * 25),
          phosphorus: Math.round(15 + Math.random() * 25),
          potassium: Math.round(15 + Math.random() * 25),
          timestamp: new Date().toISOString()
        };
      });
      
      setReadings(readingsMap);
      return farmsList;
    },
    [user?.id],
    {
      immediate: !!user?.id,
      cacheKey: user?.id ? `soil_farms_${user.id}` : undefined
    }
  );

  const { avgN, avgP, avgK, hasReadings } = useMemo(() => {
    const numReadings = Object.keys(readings).length;

    if (numReadings === 0) {
      return { avgN: 0, avgP: 0, avgK: 0, hasReadings: false };
    }

    let totalN = 0, totalP = 0, totalK = 0;
    Object.values(readings).forEach((r) => {
      totalN += r.nitrogen;
      totalP += r.phosphorus;
      totalK += r.potassium;
    });

    return {
      avgN: Math.round(totalN / numReadings),
      avgP: Math.round(totalP / numReadings),
      avgK: Math.round(totalK / numReadings),
      hasReadings: true,
    };
  }, [readings]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-lg border border-green-200 shadow mb-8 py-6 w-full">
        <Leaf className="w-16 h-16 mb-4 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-700">
          Soil Management
        </h2>
        <p className="max-w-xl mb-6 text-center text-gray-500">
          Sign in to view your soil analysis data
        </p>
        <button
          type="button"
          className="px-8 py-2 text-base font-semibold text-white transition bg-green-600 rounded-md hover:bg-green-700"
          onClick={onSignIn}
        >
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-lg border border-green-200 shadow mb-8 py-6 w-full">
        <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
        <p className="mt-4 text-lg text-gray-600">Loading soil data...</p>
      </div>
    );
  }

  const farmsWithReadings = (farms || []).filter((farm) => readings[farm.id]);

  if (!farms || farms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-lg border border-green-200 shadow mb-8 py-6 w-full">
        <Layers className="w-16 h-16 mb-4 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-700">
          No Farms Found
        </h2>
        <p className="max-w-xl mb-6 text-center text-gray-500">
          Add farms to start monitoring soil health
        </p>
      </div>
    );
  }

  return (
    <div className="container px-4 py-8 mx-auto max-w-7xl">
      <div className="flex flex-col mb-8 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 text-green-800 bg-green-100 rounded-xl">
            <Leaf className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Soil Management</h1>
            <p className="text-sm text-gray-500">Monitor your soil health</p>
          </div>
        </div>
      </div>

      {hasReadings && (
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
          <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Average Nitrogen</p>
                <p className={`text-2xl font-bold mt-1 ${avgN < 20 ? "text-red-600" : "text-green-600"}`}>
                  {avgN}%
                </p>
              </div>
              <div className={`p-3 rounded-lg ${avgN < 20 ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>
                {avgN < 20 ? <AlertTriangle className="w-6 h-6" /> : <ArrowUpRight className="w-6 h-6" />}
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Average Phosphorus</p>
                <p className={`text-2xl font-bold mt-1 ${avgP < 20 ? "text-red-600" : "text-yellow-600"}`}>
                  {avgP}%
                </p>
              </div>
              <div className={`p-3 rounded-lg ${avgP < 20 ? "bg-red-50 text-red-600" : "bg-yellow-50 text-yellow-600"}`}>
                {avgP < 20 ? <AlertTriangle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Average Potassium</p>
                <p className={`text-2xl font-bold mt-1 ${avgK < 20 ? "text-red-600" : "text-blue-600"}`}>
                  {avgK}%
                </p>
              </div>
              <div className={`p-3 rounded-lg ${avgK < 20 ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
                {avgK < 20 ? <AlertTriangle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">Your Farms</h2>
        {farmsWithReadings.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-lg shadow-sm">
            <InfoIcon className="w-12 h-12 mb-4 text-gray-400" />
            <p className="text-lg text-gray-600">No soil readings available</p>
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {farmsWithReadings.map((farm) => {
              const r = readings[farm.id];
              const isLow = r.nitrogen < 20 || r.phosphorus < 20 || r.potassium < 20;

              return (
                <div key={farm.id} className={`bg-white rounded-xl shadow-sm border ${isLow ? "border-red-200" : "border-gray-200"} overflow-hidden`}>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{farm.name}</h3>
                        <p className="text-sm text-gray-500">
                          Last tested: {new Date(r.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(isLow)}`}>
                        {isLow ? "Needs Attention" : "Optimal"}
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1 text-sm">
                          <span className="font-medium text-gray-700">Nitrogen</span>
                          <span className={`font-bold ${r.nitrogen < 20 ? "text-red-600" : "text-green-600"}`}>
                            {r.nitrogen}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div className={`${getNutrientBarColor("N")} h-2 rounded-full`} style={{ width: `${r.nitrogen}%` }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1 text-sm">
                          <span className="font-medium text-gray-700">Phosphorus</span>
                          <span className={`font-bold ${r.phosphorus < 20 ? "text-red-600" : "text-yellow-600"}`}>
                            {r.phosphorus}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div className={`${getNutrientBarColor("P")} h-2 rounded-full`} style={{ width: `${r.phosphorus}%` }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1 text-sm">
                          <span className="font-medium text-gray-700">Potassium</span>
                          <span className={`font-bold ${r.potassium < 20 ? "text-red-600" : "text-blue-600"}`}>
                            {r.potassium}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div className={`${getNutrientBarColor("K")} h-2 rounded-full`} style={{ width: `${r.potassium}%` }}></div>
                        </div>
                      </div>
                    </div>

                    {isLow && (
                      <div className="p-3 mt-4 border border-red-200 rounded-lg bg-red-50">
                        <div className="flex">
                          <AlertCircle className="text-red-500 mt-0.5 mr-2 w-4 h-4" />
                          <p className="text-sm text-red-700">
                            One or more nutrients are below optimal levels. Consider adding supplements.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}